/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  FC,
  InputHTMLAttributes,
  DetailedHTMLProps,
  forwardRef,
  ForwardedRef,
  ChangeEvent,
} from 'react'
import { v4 as uuidv4 } from 'uuid'
import { cn } from '@bem-react/classname'

import './Checkbox.scss'
import { Memo } from '@/hoc/Memo'
import { Transition } from 'react-transition-group'

const cnCheckbox = cn('Checkbox')

export type CheckboxType = 'checkbox' | 'radio'
export type CheckboxTypeView =
  | 'square'
  | 'switch'
  | 'button'
  | 'yes-no'
  | 'circle'
export type CheckboxColorType = 'light' | 'dark' | 'middle'
export type LabelPositionType = 'left' | 'right'

export interface CheckboxSchema
  extends DetailedHTMLProps<
    InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  uid?: string
  type?: CheckboxType
  typeView?: CheckboxTypeView
  label?: string
  labelPosition?: LabelPositionType
  isChecked?: boolean
  register?: any
  required?: boolean
  small?: boolean
  disabled?: boolean
  isButton?: boolean
  color?: CheckboxColorType
  textYes?: string
  textNo?: string
  bgTextYes?: string
  bgTextNo?: string
  handleCustomChange?: (e: boolean | number | string) => void
  handleCustomBlur?: (e: ChangeEvent<HTMLInputElement>) => void
  errors?: string
}

export const Checkbox: FC<CheckboxSchema> = Memo(
  forwardRef(
    (
      {
        type = 'checkbox',
        uid = `${type}-` + uuidv4(),
        typeView = 'square',
        value,
        labelPosition = 'right',
        onChange,
        onBlur,
        handleCustomChange,
        handleCustomBlur,
        label,
        name,
        // required,
        disabled,
        small,
        className,
        isButton,
        isChecked,
        color = 'light',
        textYes = 'yes',
        textNo = 'no',
        bgTextYes = '#FF3A19',
        bgTextNo = '#4F9F54',
        errors,
        ...props
      }: CheckboxSchema,
      ref: ForwardedRef<HTMLInputElement>,
    ) => {
      const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (isChecked !== undefined && handleCustomChange) {
          handleCustomChange(e.target.checked)
        } else if (handleCustomChange) {
          handleCustomChange(e.target.value)
        } else if (onChange) {
          onChange(e)
        }
      }

      // console.log(props)

      const View = (): JSX.Element => {
        switch (typeView) {
          case 'switch': {
            return (
              <div
                className={cnCheckbox(
                  {
                    switch: typeView === 'switch',
                  },
                  [className],
                )}
              >
                <input
                  name={name}
                  type={type}
                  id={uid}
                  className={cnCheckbox('input')}
                  value={value}
                  onChange={handleChange}
                  onBlur={e => {
                    handleCustomBlur && handleCustomBlur(e)
                    onBlur && onBlur(e)
                  }}
                  checked={isChecked}
                  ref={ref}
                  {...props}
                />

                <label htmlFor={uid} className={cnCheckbox('switch')}></label>
                {label && (
                  <div
                    className={cnCheckbox('label', {
                      left: labelPosition === 'left',
                      right: labelPosition === 'right',
                      disabled,
                    })}
                  >
                    {label}
                  </div>
                )}
              </div>
            )
          }
          case 'yes-no': {
            return (
              <div
                className={cnCheckbox(
                  {
                    condition: true,
                  },
                  [className],
                )}
              >
                <input
                  name={name}
                  type={type}
                  id={uid}
                  className={cnCheckbox('input')}
                  value={value}
                  onChange={handleChange}
                  checked={isChecked}
                  ref={ref}
                  {...props}
                />

                <label
                  htmlFor={uid}
                  className={cnCheckbox('label', {
                    condition: true,
                    disabled,
                  })}
                  style={{
                    backgroundColor: isChecked ? bgTextYes : bgTextNo,
                  }}
                >
                  {isChecked ? textYes : textNo}
                </label>

                <Transition
                  in={!!errors}
                  timeout={500}
                  mountOnEnter
                  unmountOnExit
                >
                  {state => (
                    <div className={cnCheckbox('error', [state])}>{errors}</div>
                  )}
                </Transition>
              </div>
            )
          }

          default: {
            return (
              <div className={cnCheckbox(null, [className])}>
                <input
                  name={name}
                  type={type}
                  id={uid}
                  className={cnCheckbox('input')}
                  value={value}
                  onChange={handleChange}
                  checked={isChecked}
                  ref={ref}
                  {...props}
                />
                {/* {label && ( */}
                <label
                  htmlFor={uid}
                  className={cnCheckbox('label', {
                    left: labelPosition === 'left',
                    right: labelPosition === 'right',
                    button: isButton,
                    disabled,
                    light: color === 'light',
                    dark: color === 'dark',
                    middle: color === 'middle',
                    nolabel: !label,
                    circle: typeView === 'circle',
                    small,
                  })}
                >
                  {label}
                </label>
                {/* )} */}

                <Transition
                  in={!!errors}
                  timeout={500}
                  mountOnEnter
                  unmountOnExit
                >
                  {state => (
                    <div className={cnCheckbox('error', [state])}>{errors}</div>
                  )}
                </Transition>
              </div>
            )
          }
        }
      }

      return <View />
    },
  ),
)
